import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Button,
  Box,
  CircularProgress,
  IconButton,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Popper,
  Checkbox,
  FormControlLabel,
  ClickAwayListener,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import dayjs from "dayjs";
import { getSnowflakeOveragesForMonth } from "../../../shared/billing.api";

export const SFOveragesModal = ({ open, month, onClose }) => {
  const [selectedMonth, setSelectedMonth] = useState(month);
  const [loading, setLoading] = useState(false);
  const [overages, setOverages] = useState([]);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [allExpanded, setAllExpanded] = useState(false);

  // Filter popper state
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filterSmsCredits, setFilterSmsCredits] = useState(true);
  const [filterMailers, setFilterMailers] = useState(false);
  const [filterContacts, setFilterContacts] = useState(false);

  useEffect(() => {
    setSelectedMonth(month);
  }, [month, open]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    getSnowflakeOveragesForMonth({
      start_date: dayjs(selectedMonth).startOf("month").format("YYYY-MM-DD"),
    })
      .then((res) => {
        const results = res?.data?.results || [];
        setOverages(results);
        // Reset expanded state on new data
        const initialExpanded = {};
        results.forEach((acct) => {
          initialExpanded[acct.account_id] = false;
        });
        setExpanded(initialExpanded);
        setAllExpanded(false);
      })
      .catch((e) => setError(e.message || "Error loading overages"))
      .finally(() => setLoading(false));
  }, [open, selectedMonth]);

  const handleClose = () => {
    onClose?.();
  };

  const handleAccordionChange = (accountId) => (event, isExpanded) => {
    setExpanded((prev) => ({
      ...prev,
      [accountId]: isExpanded,
    }));
  };

  const handleExpandCollapseAll = () => {
    const next = !allExpanded;
    const newExpanded = {};
    overages.forEach((acct) => {
      newExpanded[acct.account_id] = next;
    });
    setExpanded(newExpanded);
    setAllExpanded(next);
  };

  // Filtering logic
  const filteredOverages = overages.filter((acct) => {
    // If all filters are off, show all
    if (!filterSmsCredits && !filterMailers && !filterContacts) return true;
    // If all are on, show accounts with any of the three
    if (filterSmsCredits && filterMailers && filterContacts) {
      return Array.isArray(acct.overages)
        ? acct.overages.some(
            (ov) =>
              ov.item_description_short === "Usage: SMS Credits" ||
              ov.item_description_short === "Usage: Mailers (4x6)" ||
              ov.item_description_short === "additional_usage_base_contacts",
          )
        : false;
    }
    // If two are on
    if (filterSmsCredits && filterMailers) {
      return Array.isArray(acct.overages)
        ? acct.overages.some(
            (ov) =>
              ov.item_description_short === "Usage: SMS Credits" ||
              ov.item_description_short === "Usage: Mailers (4x6)",
          )
        : false;
    }
    if (filterSmsCredits && filterContacts) {
      return Array.isArray(acct.overages)
        ? acct.overages.some(
            (ov) =>
              ov.item_description_short === "Usage: SMS Credits" ||
              ov.item_description_short === "additional_usage_base_contacts",
          )
        : false;
    }
    if (filterMailers && filterContacts) {
      return Array.isArray(acct.overages)
        ? acct.overages.some(
            (ov) =>
              ov.item_description_short === "Usage: Mailers (4x6)" ||
              ov.item_description_short === "additional_usage_base_contacts",
          )
        : false;
    }
    // If only one is on
    if (filterSmsCredits) {
      return Array.isArray(acct.overages)
        ? acct.overages.some(
            (ov) => ov.item_description_short === "Usage: SMS Credits",
          )
        : false;
    }
    if (filterMailers) {
      return Array.isArray(acct.overages)
        ? acct.overages.some(
            (ov) => ov.item_description_short === "Usage: Mailers (4x6)",
          )
        : false;
    }
    if (filterContacts) {
      return Array.isArray(acct.overages)
        ? acct.overages.some(
            (ov) =>
              ov.item_description_short === "additional_usage_base_contacts",
          )
        : false;
    }
    return true;
  });

  return (
    <Dialog
      open={open}
      onClose={(e, r) => {
        e.stopPropagation();
        if (r === "backdropClick") return;
        handleClose();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h6">SF Overages by Account</Typography>
          </Stack>
          <Box>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Month (This should be 1 month prior to invoice month)"
              views={["month", "year"]}
              value={selectedMonth}
              onChange={(newValue) => setSelectedMonth(newValue)}
              disableFuture
              renderInput={(params) => (
                <Box component="span">{params.input}</Box>
              )}
              sx={{
                minWidth: 320,
              }}
            />
          </LocalizationProvider>
          <Button
            variant="outlined"
            size="small"
            onClick={handleExpandCollapseAll}
            sx={{ ml: 2, minWidth: 140 }}
          >
            {allExpanded ? "Collapse All" : "Expand All"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterListIcon />}
            sx={{ ml: 2 }}
            onClick={(e) =>
              setFilterAnchorEl(filterAnchorEl ? null : e.currentTarget)
            }
          >
            Filters
          </Button>
          <Popper
            open={!!filterAnchorEl}
            anchorEl={filterAnchorEl}
            placement="top-start"
            style={{ zIndex: 1300 }}
          >
            <ClickAwayListener onClickAway={() => setFilterAnchorEl(null)}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: "background.paper",
                  boxShadow: 2,
                  borderRadius: 1,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filterSmsCredits}
                      onChange={(e) => setFilterSmsCredits(e.target.checked)}
                    />
                  }
                  label="Usage: SMS Credits"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filterMailers}
                      onChange={(e) => setFilterMailers(e.target.checked)}
                    />
                  }
                  label="Usage: Mailers (4x6)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filterContacts}
                      onChange={(e) => setFilterContacts(e.target.checked)}
                    />
                  }
                  label="Contacts"
                />
              </Box>
            </ClickAwayListener>
          </Popper>
        </Stack>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Box>
            {filteredOverages.length === 0 ? (
              <Typography>No overages found for this month.</Typography>
            ) : (
              <Stack spacing={2}>
                {filteredOverages.map((acct) => {
                  // Find all overages that match the selected filters
                  const overageTypes = [];
                  if (filterSmsCredits) overageTypes.push("Usage: SMS Credits");
                  if (filterMailers) overageTypes.push("Usage: Mailers (4x6)");
                  if (filterContacts)
                    overageTypes.push("additional_usage_base_contacts");

                  const matchingOverages = Array.isArray(acct.overages)
                    ? acct.overages.filter((ov) =>
                        overageTypes.includes(ov.item_description_short),
                      )
                    : [];

                  return (
                    <Accordion
                      key={acct.account_id}
                      expanded={!!expanded[acct.account_id]}
                      onChange={handleAccordionChange(acct.account_id)}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "bold" }}
                        >
                          {acct.account_name} (ID: {acct.account_id})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Divider sx={{ my: 1 }} />
                        {matchingOverages.length > 0 ? (
                          matchingOverages.map((overage, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                background:
                                  overage.overage_usage > 0
                                    ? "#fff9f0"
                                    : "transparent",
                                borderRadius: 1,
                                p: 1,
                                mb: 2,
                              }}
                            >
                              <Typography>
                                <strong>
                                  {overage.item_description_short}
                                </strong>
                              </Typography>
                              <Typography>
                                <strong>Overage Usage:</strong>{" "}
                                <span
                                  style={{
                                    color:
                                      overage.overage_usage > 0
                                        ? "#d32f2f"
                                        : undefined,
                                    fontWeight:
                                      overage.overage_usage > 0
                                        ? "bold"
                                        : "normal",
                                  }}
                                >
                                  {typeof overage.overage_usage === "number"
                                    ? overage.overage_usage
                                    : "--"}
                                </span>
                              </Typography>
                              <Typography>
                                <strong>Unit Cost:</strong>{" "}
                                {typeof overage.overage_per_unit === "number"
                                  ? `$${overage.overage_per_unit.toFixed(4)}`
                                  : "--"}
                              </Typography>
                              <Typography>
                                <strong>Total Overage Cost:</strong>{" "}
                                {typeof overage.overage_usage === "number" &&
                                typeof overage.overage_per_unit === "number"
                                  ? `$${(
                                      overage.overage_usage *
                                      overage.overage_per_unit
                                    ).toFixed(2)}`
                                  : "--"}
                              </Typography>
                              {overage.item_description_short ===
                                "additional_usage_base_contacts" && (
                                <Typography>
                                  <strong>Latest Billing Amount:</strong>{" "}
                                  {typeof overage.latest_billing_amount ===
                                  "number"
                                    ? `$${overage.latest_billing_amount.toFixed(
                                        2,
                                      )}`
                                    : "--"}
                                </Typography>
                              )}
                            </Box>
                          ))
                        ) : (
                          <Typography>
                            No overages for selected filters for this account.
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Stack>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
